import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Avatar } from '@kula-ai/components';
import { KText } from '@/ui-library/components/text';
import { UNDERWAY_POPUP_STRINGS } from './underwayPopup.strings';
import type { UnderwayActivity } from './types';

const STR = UNDERWAY_POPUP_STRINGS.candidatesModal;

export type SelectedCandidatesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  activity: UnderwayActivity;
};

const ValuePill = ({ children }: { children: React.ReactNode }) => (
  <Box
    as="span"
    display="inline-flex"
    alignItems="center"
    bg="secondary-50"
    color="secondary-700"
    borderRadius="6px"
    px="8px"
    py="3px"
    whiteSpace="nowrap"
    lineHeight="1.4"
  >
    <KText size="sm-regular" color="secondary-700">
      {children}
    </KText>
  </Box>
);

export const SelectedCandidatesModal = ({
  isOpen,
  onClose,
  activity,
}: SelectedCandidatesModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="3xl">
      <ModalOverlay />
      <ModalContent
        borderRadius="6px"
        maxH="80vh"
        data-test-id="automation-selected-candidates-modal"
      >
        <ModalHeader px="12px" pb="0">
          <Flex alignItems="center" gap="10px">
            <Box bg="secondary-100" borderRadius="6px" px="8px" py="2px">
              <KText size="sm-semi-bold" color="secondary-700">
                {activity.candidates.length}
              </KText>
            </Box>
            <KText size="md-semi-bold" color="secondary-900">
              {STR.header(activity.activityLabel, activity.stageName)}
            </KText>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto" px="12px" pt="16px" pb="24px">
          <Table size="sm" variant="simple">
            <Thead position="sticky" top={0} bg="base-white" zIndex={1}>
              <Tr>
                <Th
                  borderTop="1px solid"
                  borderBottom="1px solid"
                  borderColor="secondary-100"
                  py="10px"
                  pl={0}
                  pr="8px"
                >
                  <KText
                    size="sm-semi-bold"
                    fontWeight="bold"
                    color="secondary-600"
                    textTransform="none"
                  >
                    {STR.columns.candidate}
                  </KText>
                </Th>
                <Th
                  borderTop="1px solid"
                  borderBottom="1px solid"
                  borderColor="secondary-100"
                  py="10px"
                  px="8px"
                >
                  <KText
                    size="sm-semi-bold"
                    fontWeight="bold"
                    color="secondary-600"
                    textTransform="none"
                  >
                    {STR.columns.job}
                  </KText>
                </Th>
                <Th
                  borderTop="1px solid"
                  borderBottom="1px solid"
                  borderColor="secondary-100"
                  py="10px"
                  px="8px"
                >
                  <KText
                    size="sm-semi-bold"
                    fontWeight="bold"
                    color="secondary-600"
                    textTransform="none"
                  >
                    {STR.columns.stage}
                  </KText>
                </Th>
              </Tr>
            </Thead>
            <Tbody data-test-id="automation-selected-candidates-list">
              {activity.candidates.map((c) => (
                <Tr key={c.id}>
                  <Td
                    borderBottom="1px solid"
                    borderColor="secondary-100"
                    pl={0}
                    pr="8px"
                  >
                    <Flex alignItems="center" gap="10px">
                      <Avatar size="xs" name={c.name} src={c.avatarUrl} />
                      {c.profileUrl ? (
                        <KText
                          as="a"
                          href={c.profileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="sm-medium"
                          color="secondary-900"
                          cursor="pointer"
                          textDecoration="none"
                          _hover={{
                            color: 'primary-600',
                            textDecoration: 'underline',
                          }}
                        >
                          {c.name}
                        </KText>
                      ) : (
                        <KText size="sm-medium" color="secondary-900">
                          {c.name}
                        </KText>
                      )}
                    </Flex>
                  </Td>
                  <Td
                    borderBottom="1px solid"
                    borderColor="secondary-100"
                    px="8px"
                  >
                    <ValuePill>{activity.jobName}</ValuePill>
                  </Td>
                  <Td
                    borderBottom="1px solid"
                    borderColor="secondary-100"
                    px="8px"
                  >
                    <ValuePill>{activity.stageName}</ValuePill>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
